import api from './api';

/**
 * When true: try real API first, fallback to mock on failure (404, network error, etc.).
 * When false: only real API, no fallback.
 */
const USE_MOCK_FALLBACK = true;
const MOCK_LATENCY_MS = 300;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// ---------------------------------------------------------------------------
// Mock Data: POST /dashboards/list
// ---------------------------------------------------------------------------

const MOCK_DASHBOARD_LIST = {
  'CONTACT__OVERVIEW': {
    data: [
      {
        dashboard_uuid: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
        name: 'Customer Support Overview',
        description: 'Main view for support agents handling customer tickets.',
        type: 'MANAGED',
        is_default: true,
        is_user_default: false,
        permission: 'viewer',
        published_at: '2025-11-03T14:22:00Z',
        published_by: { user_id: 12, name: 'Laura Gomez' },
      },
    ],
  },
};

// ---------------------------------------------------------------------------
// Mock Data: POST /dashboards/layout
// ---------------------------------------------------------------------------

const MOCK_DASHBOARD_LAYOUTS = {
  'a1b2c3d4-e5f6-7890-abcd-ef1234567890': {
    data: {
      dashboard_uuid: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
      name: 'Customer Support Overview',
      version: 'PUBLISHED',
      published_at: '2025-11-03T14:22:00Z',
      filters: [
        {
          filter_uuid: 'f1f1f1f1-0000-0000-0000-000000000001',
          name: 'DATE_RANGE',
          label: 'Date Range',
          default_value: null,
          schema: {
            type: 'object',
            properties: {
              from: { type: 'string', format: 'date' },
              to: { type: 'string', format: 'date' },
            },
            required: ['from', 'to'],
          },
          ui_schema: {
            from: { 'ui:widget': 'date', 'ui:label': 'From' },
            to: { 'ui:widget': 'date', 'ui:label': 'To' },
          },
          data_source: null,
        },
      ],
      widgets: [
        {
          instance_uuid: 'w1-notes-0001',
          widget_definition_uuid: 'd1-notes-def',
          widget_type: 'CUSTOM_FIXED',
          visualization_type: null,
          accepts_global_filters: false,
          refresh_interval: null,
          layout: { x: 0, y: 0, w: 4, h: 7 },
          constraints: { minW: 4, maxW: 8, minH: 7, maxH: 9 },
          config: {
            card: {
              title: 'Notes',
              description: '',
              header_buttons: [
                {
                  key: 'BTN_EXPAND',
                  icon: 'arrowOutward',
                  tooltip: 'View all',
                  action: {
                    type: 'NAVIGATE',
                    target: 'activity',
                  },
                },
                {
                  key: 'BTN_ADD',
                  icon: 'plus',
                  tooltip: 'Create Note',
                  action: {
                    type: 'SLIDEOUT',
                    target: 'create_note',
                    params_from: 'CONTEXT',
                  },
                },
              ],
            },
            datasource: {
              datasource_uuid: 'ds-notes-001',
              type: 'INTERNAL_API',
              endpoint: '/v1/notes/list',
              query: null,
              static_params: {},
            },
            pagination: {
              enabled: false,
              default_page_size: null,
              page_size_options: null,
            },
            visualization: {
              content_actions: [],
            },
          },
        },
        {
          instance_uuid: 'w2-activity-0001',
          widget_definition_uuid: 'd2-activity-def',
          widget_type: 'CUSTOM_FIXED',
          visualization_type: null,
          accepts_global_filters: true,
          refresh_interval: 60,
          layout: { x: 4, y: 0, w: 8, h: 7 },
          constraints: { minW: 4, maxW: 8, minH: 7, maxH: 9 },
          config: {
            card: {
              title: 'Activity',
              description: '',
              header_buttons: [],
            },
            datasource: {
              datasource_uuid: 'ds-activity-001',
              type: 'INTERNAL_API',
              endpoint: '/v1/activity/list',
              query: null,
              static_params: {},
            },
            pagination: {
              enabled: false,
              default_page_size: null,
              page_size_options: null,
            },
            visualization: {
              content_actions: [],
            },
          },
        },
        // TODO: STANDARD widgets (TABLE, METRIC, CARDS) will be added here once Phase 6 renderers are ready
      ],
    },
  },
};

// ---------------------------------------------------------------------------
// POST /dashboards/list
// ---------------------------------------------------------------------------

export const fetchDashboardList = async (scopeType, module) => {
  try {
    const response = await api.post('/v1/dashboards/list', {
      scope_type: scopeType,
      module,
    });
    return response.data;
  } catch (err) {
    if (!USE_MOCK_FALLBACK) throw err;
    await delay(MOCK_LATENCY_MS);
    const key = `${scopeType}__${module}`;
    return MOCK_DASHBOARD_LIST[key] || { data: [] };
  }
};

// ---------------------------------------------------------------------------
// POST /dashboards/layout
// ---------------------------------------------------------------------------

export const fetchDashboardLayout = async (
  dashboardUuid,
  version = 'PUBLISHED',
  breakpoint = 'DESKTOP',
) => {
  try {
    const response = await api.post('/v1/dashboards/layout', {
      dashboard_uuid: dashboardUuid,
      version,
      breakpoint,
    });
    return response.data;
  } catch (err) {
    if (!USE_MOCK_FALLBACK) throw err;
    await delay(MOCK_LATENCY_MS);
    const layout = MOCK_DASHBOARD_LAYOUTS[dashboardUuid];
    if (!layout) {
      const error = new Error('Dashboard not found');
      error.code = 'DASHBOARD_NOT_FOUND';
      throw error;
    }
    return layout;
  }
};
